import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  DialogContent,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Paper,
  Popover,
  Select,
  Tooltip,
  Typography
} from "@mui/material";
import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";

import { SessionContext } from "../../../Context/SessionContext";
import { UserContext } from "../../../Context/UserContext";
import { BusinessUnit } from "../../../Models/BusinessUnit";
import { Customer, EnhancedCustomer } from "../../../Models/Customer";
import { Lead, LeadResult, LeadStatus } from "../../../Models/Lead";
import { MarketingCampaign } from "../../../Models/MarketingCampaign";
import { Opportunity } from "../../../Models/Opportunity";
import { StepId, Steps } from "../../../Types/Step";
import { LeadResultType } from "../../../Utils/Enums";
import { Error } from "../../../Utils/Errors";
import { useApi } from "../../../Utils/useApi";
import useGetCustomer from "../../../V2/Playbook/Search/Hooks/UseGetCustomer";
import AlertGridItem from "../../Helpers/AlertGridItem";
import Modal from "../../Helpers/Modal";
import { PhoneFieldMasked } from "../../Helpers/PhoneNumberField";
import { SearchParams } from "./Search/Search";
import CustomerTable from "./Search/SearchCustomerTable";
import SearchForm from "./Search/SearchForm";

const validIvrUnavailableReason = process.env.REACT_APP_IVR_OUTBOUND_REASON || "Callback";

const Manual: React.FC = () => {
  const { areasOfInterest, businessUnits, centers, ivrState, leadResultTypes, user } = useContext(UserContext);
  const [badLeadResult] = useState(leadResultTypes.find(lrt => lrt.name === LeadResultType.BadLead));

  const {
    isSearchComplete,
    lead,
    phoneNumberErrors,
    previewCustomer,
    setSelectedCustomer,
    setIsSearchComplete,
    setManualLead,
    setLeadResult,
    setStep,
    setPreviewCustomer,
    helpers: { endSession, loadFromLocal, saveToLocal }
  } = useContext(SessionContext);
  // on unmount, clear the preview customer
  useEffect(() => () => setPreviewCustomer(undefined), []);

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [businessUnit, setBusinessUnit] = useState<BusinessUnit>();
  const [completed, setCompleted] = useState(false);
  const [leadUpdate, setLeadUpdate] = useState<Lead>();
  const [leadResult, setPostLeadResult] = useState<LeadResult>();
  const [isModalOpen, setModalOpen] = useState(false);
  const [isNewOpportunity, setIsNewOpportunity] = useState(false);
  const [opportunities, setOpportunities] = useState(
    loadFromLocal<Record<string, Opportunity[]>>("searchCustomerOppos") || {}
  );
  const [phoneNumber, setPhoneNumber] = useState("");
  const [searchCustomers, setSearchCustomers] = useState<Customer[]>(lead.customers || []);

  const previewCustomerRef = useRef(previewCustomer);
  useEffect(() => {
    previewCustomerRef.current = previewCustomer;
  }, [previewCustomer]);

  // POST INITIAL LEAD FROM SEARCH PARAMS
  const { response: newLeadResponse, isLoading: newLeadLoading } = useApi<Lead, Lead>({
    method: "post",
    url: "Legacy/V0/contacts/manual",
    data: lead,
    valid: (!!lead.firstName || !!lead.lastName || !!lead.phoneNumber || !!lead.email) && !lead.id,
    error: Error.PostLeadManual
  });
  useEffect(() => {
    if (newLeadResponse) {
      setManualLead && setManualLead(newLeadResponse);
      setSearchCustomers(newLeadResponse.customers);
    }
  }, [newLeadResponse, setManualLead]);

  // PUT LEAD ON PARAMS CHANGE
  const { response: leadResponse, isLoading: leadLoading } = useApi<Lead, Lead>({
    method: "put",
    url: `Legacy/V0/contacts/${leadUpdate?.id}/manual`,
    data: leadUpdate,
    valid: !!leadUpdate,
    error: Error.PutLead
  });
  useEffect(() => {
    if (leadResponse) {
      setManualLead && setManualLead(leadResponse);
      if (leadResponse.status === LeadStatus.New) setSearchCustomers(leadResponse.customers);
      else if (leadResponse.status === LeadStatus.AgentPickedUp) {
        setSelectedCustomer();
        setIsSearchComplete(true);
        setStep(Steps[StepId.Intro]);
      }
    }
  }, [leadResponse, setIsSearchComplete, setManualLead, setSelectedCustomer, setStep]);

  // ON PREVIEWING A CUSTOMER, QUERY DEEP VALUES IF SHALLOW
  const {
    result: customerResponse,
    isLoading: customerLoading,
    execute: setRequest
  } = useGetCustomer({ areaOfInterestOptions: areasOfInterest, centerOptions: centers });
  useEffect(() => {
    if (previewCustomer?.shallow && previewCustomer?.id)
      setRequest({ customerId: previewCustomer.id, contactId: lead.id });
  }, [previewCustomer]);
  useEffect(() => {
    if (!customerResponse) return;
    // update the search customers
    setSearchCustomers(oldCustomers => {
      const customers = [...oldCustomers];
      customers[customers.findIndex(c => c.id === customerResponse.id)] = customerResponse;
      saveToLocal(customers, "searchCustomers");
      return customers;
    });
    // if previewing, update preview customer
    setPreviewCustomer(pc =>
      pc?.id === customerResponse.id && pc.shallow
        ? new EnhancedCustomer({
            ...customerResponse,
            opportunity: null,
            center: customerResponse.centerId ? centers.find(c => c.id === customerResponse.centerId) : undefined
          })
        : pc
    );
  }, [customerResponse]);

  // ON PREVIEWING A CUSTOMER
  const { response: opposResponse } = useApi<Opportunity[]>({
    url: `Legacy/V0/Contacts/${lead.id}/Customers/${previewCustomer?.id}/Opportunities`,
    isCancelable: false,
    valid: !!previewCustomer?.id && !opportunities[previewCustomer.id],
    error: Error.GetCustomerOpportunities
  });
  useEffect(() => {
    if (opposResponse) {
      // update the search customer opportunities
      setOpportunities(oppos => {
        const newOppos = previewCustomerRef.current?.id
          ? { ...oppos, [previewCustomerRef.current.id]: opposResponse }
          : oppos;
        saveToLocal(newOppos, "searchCustomerOppos");
        return newOppos;
      });
    }
  }, [opposResponse]);

  // PUT/POST LEAD RESPONSE ON CANCEL
  const {
    response: leadResultResponse,
    error: leadResultError,
    isLoading: leadResultLoading
  } = useApi<LeadResult, LeadResult>({
    method: leadResult?.id ? "put" : "post",
    url: `Legacy/V0/Contacts/${lead.id}/Result`,
    data: leadResult as LeadResult,
    valid: !!leadResult,
    error: leadResult?.id ? Error.PutLeadResult : Error.PostLeadResult
  });
  useEffect(() => {
    if (leadResultResponse) {
      setLeadResult(leadResultResponse);
      endSession();
    }
    const leadResult = leadResultError?.response?.data?.data;
    if (leadResult) {
      setLeadResult(leadResult as LeadResult);
      endSession();
    }
  }, [leadResultResponse, leadResultError, endSession]);

  // POST DIAL ON LEAD STATUS === AGENT SENT
  const { response: dialResponse } = useApi({
    method: "post",
    url: `Legacy/V0/contacts/${lead.id}/dial`,
    valid: lead.status === LeadStatus.AgentSent && !lead.ivrContactId,
    error: Error.PostLeadDial
  });
  useEffect(() => {
    if (dialResponse) toast.success("A dial command has been successfully sent to MAX.");
  }, [dialResponse]);

  // ON LEAD CHANGE
  useEffect(() => {
    // if lead status is sent
    if (lead.status === LeadStatus.AgentSent) {
      // and if ivrContactId is set, start a final PUT
      if (lead.ivrContactId) setLeadUpdate({ ...lead, status: LeadStatus.AgentPickedUp });
      // else set UI to completed
      else setCompleted(true);
    }
  }, [lead]);

  const handleNewGuest = () => {
    setPreviewCustomer(undefined);
    setModalOpen(true);
  };
  const handleProceed = () => (previewCustomer?.opportunity ? handleConfirm() : setModalOpen(true));
  const handleCancelLead = () =>
    lead.id
      ? setPostLeadResult({
          id: lead.result?.id,
          type: badLeadResult?.id ?? 0,
          customerId: null,
          appointmentId: null,
          opportunityId: null,
          notOfferedProcedures: {},
          notes: null,
          disqualifyingReasons: {}
        })
      : endSession();
  const handleSearchParams = (params?: SearchParams) => {
    if (!params) return setLeadUpdate(undefined);
    const newLead: Lead = {
      ...lead,
      firstName: params.firstName || null,
      lastName: params.lastName || null,
      phoneNumber: params.phoneNumber || null,
      email: params.email || null
    };
    if (lead.id) setLeadUpdate(newLead);
    else setManualLead && setManualLead(newLead);
  };
  const handleOpportunity = useCallback(() => setIsNewOpportunity(false), []);
  const toggleNewOpportunity = () => {
    setIsNewOpportunity(v => !v);
    setPreviewCustomer(pc => (pc && { ...pc, opportunity: null }) || undefined);
  };
  const handleConfirm = () => {
    setLeadUpdate({
      ...lead,
      status: lead.ivrContactId ? LeadStatus.AgentPickedUp : LeadStatus.AgentSent,
      phoneNumber: lead.phoneNumber || previewCustomer?.phoneNumber || phoneNumber,
      campaign:
        previewCustomer?.opportunity?.campaign ||
        new MarketingCampaign({
          id: businessUnit?.defaultMarketingCampaignId as string,
          businessUnit: businessUnit as BusinessUnit
        })
    });
    if (!previewCustomer)
      setPreviewCustomer(
        new EnhancedCustomer({
          opportunity: {
            campaign: new MarketingCampaign({
              id: businessUnit?.defaultMarketingCampaignId as string,
              businessUnit: businessUnit as BusinessUnit
            }),
            createdByUserId: user.id
          }
        })
      );
  };
  const invalidIvrState = ivrState.state !== "Unavailable" || ivrState.reason !== validIvrUnavailableReason;
  const phoneErrors = Object.entries(phoneNumberErrors).filter(([, value]) => value);

  return (
    <Paper sx={{ padding: 2 }}>
      {leadResultLoading ? (
        <Grid container item xs alignItems="center" justifyContent="center">
          <CircularProgress sx={{ padding: 5 }} />
        </Grid>
      ) : (
        <Grid container direction="column" spacing={2}>
          <Grid item>
            <Typography variant="body1" align="center">
              Please locate a customer and select an opportunity to begin.
            </Typography>
          </Grid>
          <Grid item>
            <CustomerTable
              customers={searchCustomers}
              opportunities={customerLoading ? {} : opportunities}
              disabled={newLeadLoading || leadLoading}
              selectedCustomerId={previewCustomer?.id || undefined}
              selectedOpportunityId={previewCustomer?.opportunity?.id}
              setPreviewCustomer={setPreviewCustomer}
              setOpportunity={handleOpportunity}
            />
          </Grid>
          <AlertGridItem severity="info" in={Boolean(phoneErrors.length)} spacing={2}>
            {`${phoneErrors.length} phone ${
              phoneErrors.length > 1 ? "numbers are" : "number is"
            } not allowed. See the ribbon for details.`}
          </AlertGridItem>
          {Boolean(previewCustomer) && (
            <Grid item>
              <FormControlLabel
                control={<Checkbox color="primary" />}
                label="No Match, Create New Opportunity"
                labelPlacement="end"
                onChange={toggleNewOpportunity}
                checked={isNewOpportunity}
              />
            </Grid>
          )}
          <Grid item>
            <SearchForm loading={newLeadLoading} setSearchParams={handleSearchParams} />
          </Grid>
          <Grid item container justifyContent="space-between">
            {(completed && (
              <Grid item>
                <Typography variant="body1" align="center">
                  This step has been completed. Please wait for the call in MAX to begin.
                </Typography>
                <Typography variant="body1" align="center">
                  <strong>
                    If the call does not begin within a few seconds, please ensure that you are logged into MAX, and
                    dial the number manually to continue.
                  </strong>
                </Typography>
              </Grid>
            )) || (
              <>
                <Grid item>
                  <Tooltip
                    title={
                      (invalidIvrState && (
                        <>
                          <div>To continue you must be </div>
                          <div>{`Unavailable: ${validIvrUnavailableReason} in MAX.`}</div>
                        </>
                      )) ||
                      ""
                    }
                  >
                    <span>
                      <Button
                        disabled={!lead.id || leadLoading || invalidIvrState || Boolean(phoneErrors.length)}
                        onClick={handleNewGuest}
                      >
                        No Match, Create New Guest
                      </Button>
                    </span>
                  </Tooltip>
                </Grid>
                {!isSearchComplete && (
                  <>
                    <Grid item>
                      <Button
                        color="secondary"
                        disabled={newLeadLoading || leadLoading}
                        onClick={e => setAnchorEl(e.currentTarget)}
                      >
                        Cancel
                      </Button>
                    </Grid>
                    <Popover
                      open={!!anchorEl}
                      anchorEl={anchorEl}
                      onClose={() => setAnchorEl(null)}
                      anchorOrigin={{ vertical: "top", horizontal: "center" }}
                      transformOrigin={{ vertical: "bottom", horizontal: "center" }}
                    >
                      <Box p={2}>
                        <Grid container direction="column" spacing={1}>
                          <Grid item>
                            <Typography variant="body2">End the session?</Typography>
                          </Grid>
                          <Grid container item xs justifyContent="center">
                            <Button onClick={handleCancelLead}>Confirm</Button>
                          </Grid>
                        </Grid>
                      </Box>
                    </Popover>
                  </>
                )}
                <Grid item>
                  {((!!previewCustomer?.shallow || leadLoading) && <CircularProgress />) || (
                    <Tooltip
                      title={
                        (invalidIvrState && (
                          <>
                            <div>To continue you must be </div>
                            <div>{`Unavailable: ${validIvrUnavailableReason} in MAX.`}</div>
                          </>
                        )) ||
                        (!previewCustomer?.opportunity && !isNewOpportunity && "Select a guest and an opportunity.") ||
                        ""
                      }
                    >
                      <span>
                        <Button
                          onClick={handleProceed}
                          disabled={
                            (!previewCustomer?.opportunity && !isNewOpportunity) ||
                            invalidIvrState ||
                            Boolean(phoneNumberErrors.phoneNumber)
                          }
                        >
                          Proceed
                        </Button>
                      </span>
                    </Tooltip>
                  )}
                </Grid>
              </>
            )}
          </Grid>
        </Grid>
      )}
      <Modal
        open={isModalOpen}
        handleClose={leadLoading ? undefined : () => setModalOpen(false)}
        confirmDisabled={!businessUnit}
        confirmSpinner={leadLoading}
        handleConfirm={handleConfirm}
        title="Select an Opportunity"
      >
        <DialogContent>
          <Grid container spacing={1} direction="column">
            {!lead.phoneNumber && (
              <>
                <Grid item>
                  <Typography variant="body1" align="center">
                    Please enter the phone number to be dialed.
                  </Typography>
                </Grid>
                <Grid item>
                  <FormControl variant="outlined" margin="dense" size="small" fullWidth sx={{ m: 0 }}>
                    <InputLabel htmlFor="formatted-text-mask-input">Phone</InputLabel>
                    <OutlinedInput
                      margin="dense"
                      size="small"
                      value={phoneNumber}
                      onChange={e => setPhoneNumber(e.target.value.replace(/[^0-9]/g, ""))}
                      onPaste={e => setPhoneNumber(e.clipboardData.getData("text").replace(/[^0-9]/g, ""))}
                      inputComponent={PhoneFieldMasked as never}
                    />
                  </FormControl>
                </Grid>
              </>
            )}
            {!previewCustomer?.opportunity && (
              <>
                <Grid item>
                  <Typography variant="body1">Please select a new business unit.</Typography>
                </Grid>
                <Grid item>
                  <FormControl sx={{ minWidth: "20rem" }}>
                    <InputLabel>Business Unit</InputLabel>
                    <Select
                      label="Business Unit"
                      value={businessUnit?.id || ""}
                      onChange={({ target: { value } }) =>
                        setBusinessUnit(businessUnits.find(({ id }) => id === value))
                      }
                      displayEmpty
                      sx={{ minWidth: "15rem" }}
                    >
                      {businessUnits
                        .filter(bu => bu.defaultMarketingCampaignId)
                        .map(({ id, name }, index) => (
                          <MenuItem
                            value={id}
                            disabled={
                              !!previewCustomer?.id &&
                              !!opportunities[previewCustomer.id] &&
                              opportunities[previewCustomer.id].some(
                                ({ campaign: { businessUnit } }) => businessUnit.id === id
                              )
                            }
                            key={index}
                          >
                            {name}
                          </MenuItem>
                        ))}
                    </Select>
                  </FormControl>
                </Grid>
              </>
            )}
          </Grid>
        </DialogContent>
      </Modal>
    </Paper>
  );
};

export default Manual;
